import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from './ui/alert-dialog';
import { Download, FileSpreadsheet, FileText, Calendar, Building2, Loader2, Check } from 'lucide-react';
import { format, subDays, startOfMonth, endOfMonth, startOfYear } from 'date-fns';

const PRESETS = [
  { label: 'Yesterday', fn: () => { const y = subDays(new Date(), 1); return [y, y]; } },
  { label: 'Last 7 Days', fn: () => [subDays(new Date(), 7), subDays(new Date(), 1)] },
  { label: 'This Month', fn: () => [startOfMonth(new Date()), new Date()] },
  { label: 'Last Month', fn: () => { const d = subDays(startOfMonth(new Date()), 1); return [startOfMonth(d), d]; } },
  { label: 'YTD', fn: () => [startOfYear(new Date()), new Date()] },
];

export default function ReportExportModal({ open, onClose, portfolioId }) {
  const { getHeaders, API } = useAuth();
  const [outlets, setOutlets] = useState([]);
  const [selectedOutlets, setSelectedOutlets] = useState([]);
  const [startDate, setStartDate] = useState(format(subDays(new Date(), 7), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(subDays(new Date(), 1), 'yyyy-MM-dd'));
  const [exportFormat, setExportFormat] = useState('xlsx');
  const [exporting, setExporting] = useState(false);
  const [activePreset, setActivePreset] = useState('Last 7 Days');

  useEffect(() => {
    if (open) {
      axios.get(`${API}/outlets`, { headers: getHeaders() })
        .then(res => {
          setOutlets(res.data || []);
          setSelectedOutlets((res.data || []).map(o => o.id));
        })
        .catch(() => {});
    }
  }, [open, API, getHeaders]);

  const toggleOutlet = (id) => {
    setSelectedOutlets(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const selectAll = () => setSelectedOutlets(outlets.map(o => o.id));
  const deselectAll = () => setSelectedOutlets([]);

  const applyPreset = (preset) => {
    const [s, e] = preset.fn();
    setStartDate(format(s, 'yyyy-MM-dd'));
    setEndDate(format(e, 'yyyy-MM-dd'));
    setActivePreset(preset.label);
  };

  const handleExport = async () => {
    if (selectedOutlets.length === 0) {
      toast.error('Select at least one outlet');
      return;
    }
    if (!startDate || !endDate || startDate > endDate) {
      toast.error('Invalid date range');
      return;
    }
    setExporting(true);
    try {
      const params = new URLSearchParams({
        start_date: startDate,
        end_date: endDate,
        format: exportFormat,
      });
      if (selectedOutlets.length < outlets.length) {
        params.set('outlet_ids', selectedOutlets.join(','));
      }
      if (portfolioId) params.set('portfolio_id', portfolioId);

      const response = await axios.get(`${API}/reports/export?${params.toString()}`, {
        headers: getHeaders(),
        responseType: 'blob',
      });

      // Download
      const ext = exportFormat === 'csv' ? 'csv' : 'xlsx';
      const mime = exportFormat === 'csv' ? 'text/csv' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const blob = new Blob([response.data], { type: mime });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `report_${startDate}_to_${endDate}.${ext}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(`Report exported as ${ext.toUpperCase()}`);
      onClose();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Export failed';
      toast.error(typeof msg === 'string' ? msg : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <AlertDialogContent className="border-[#E2E8DF] max-w-lg max-h-[85vh] overflow-y-auto">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-[#1A2421] flex items-center gap-2">
            <Download className="w-5 h-5 text-[#2D6A4F]" /> Export Report
          </AlertDialogTitle>
          <AlertDialogDescription className="text-[#5C6B64] text-sm">
            Download sales & operations data for a date range across outlets.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-4 py-2">
          {/* Date Range Presets */}
          <div>
            <Label className="text-xs text-[#5C6B64] mb-1.5 block">Date Range</Label>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {PRESETS.map(p => (
                <button key={p.label} onClick={() => applyPreset(p)}
                  className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                    activePreset === p.label
                      ? 'bg-[color:var(--color-primary)] text-white'
                      : 'bg-[#F3F5F2] text-[#5C6B64] hover:bg-[#E2E8DF]'
                  }`}
                  data-testid={`preset-${p.label.toLowerCase().replace(/\s/g, '-')}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] text-[#5C6B64]">From</Label>
                <input type="date" value={startDate} onChange={e => { setStartDate(e.target.value); setActivePreset(''); }}
                  className="w-full h-9 px-2.5 rounded-md border border-[#E2E8DF] text-sm text-[#1A2421] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
                  data-testid="report-start-date" />
              </div>
              <div>
                <Label className="text-[10px] text-[#5C6B64]">To</Label>
                <input type="date" value={endDate} onChange={e => { setEndDate(e.target.value); setActivePreset(''); }}
                  className="w-full h-9 px-2.5 rounded-md border border-[#E2E8DF] text-sm text-[#1A2421] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
                  data-testid="report-end-date" />
              </div>
            </div>
          </div>

          {/* Outlet Selection */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <Label className="text-xs text-[#5C6B64]">Outlets</Label>
              <div className="flex gap-2">
                <button onClick={selectAll} className="text-[10px] text-[#2D6A4F] hover:underline" data-testid="select-all-outlets">All</button>
                <button onClick={deselectAll} className="text-[10px] text-[#C45A41] hover:underline" data-testid="deselect-all-outlets">None</button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-1.5 max-h-[140px] overflow-y-auto border border-[#E2E8DF] rounded-lg p-2">
              {outlets.map(o => (
                <button key={o.id} onClick={() => toggleOutlet(o.id)}
                  className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs text-left transition-colors ${
                    selectedOutlets.includes(o.id)
                      ? 'bg-[color:var(--color-primary)]/10 text-[color:var(--color-primary)] font-medium'
                      : 'text-[#5C6B64] hover:bg-[#F3F5F2]'
                  }`}
                  data-testid={`outlet-toggle-${o.id}`}
                >
                  {selectedOutlets.includes(o.id) ? (
                    <Check className="w-3 h-3 text-[#2D6A4F] shrink-0" />
                  ) : (
                    <div className="w-3 h-3 rounded border border-[#E2E8DF] shrink-0" />
                  )}
                  <span className="truncate">{o.name}</span>
                </button>
              ))}
            </div>
            <p className="text-[10px] text-[#5C6B64] mt-1">{selectedOutlets.length} of {outlets.length} selected</p>
          </div>

          {/* Format Selection */}
          <div>
            <Label className="text-xs text-[#5C6B64] mb-1.5 block">Format</Label>
            <div className="flex gap-2">
              <button onClick={() => setExportFormat('xlsx')}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg border-2 transition-colors text-sm font-medium ${
                  exportFormat === 'xlsx'
                    ? 'border-[color:var(--color-primary)] bg-[color:var(--color-primary)]/5 text-[color:var(--color-primary)]'
                    : 'border-[#E2E8DF] text-[#5C6B64] hover:border-[color:var(--color-primary)]/30'
                }`}
                data-testid="format-xlsx"
              >
                <FileSpreadsheet className="w-4 h-4" />
                Excel (.xlsx)
              </button>
              <button onClick={() => setExportFormat('csv')}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg border-2 transition-colors text-sm font-medium ${
                  exportFormat === 'csv'
                    ? 'border-[color:var(--color-primary)] bg-[color:var(--color-primary)]/5 text-[color:var(--color-primary)]'
                    : 'border-[#E2E8DF] text-[#5C6B64] hover:border-[color:var(--color-primary)]/30'
                }`}
                data-testid="format-csv"
              >
                <FileText className="w-4 h-4" />
                CSV
              </button>
            </div>
          </div>

          {/* Report Contents Summary */}
          <div className="bg-[#F9F9F8] border border-[#E2E8DF] rounded-lg p-3">
            <p className="text-[10px] uppercase tracking-wider text-[#5C6B64] font-medium mb-1.5">Report includes</p>
            <div className="flex flex-wrap gap-1.5">
              {['Sales & Budget', 'Food/Bev Cost %', 'Inventory', 'Store Issues',
                'Marketing Spend', 'Maintenance', 'Compliance %'].map(tag => (
                <Badge key={tag} variant="outline" className="text-[10px] border-[#E2E8DF] text-[#5C6B64]">{tag}</Badge>
              ))}
            </div>
            {exportFormat === 'xlsx' && (
              <p className="text-[10px] text-[#5C6B64] mt-1.5">Excel includes 2 sheets: Daily Detail + Outlet Summary</p>
            )}
          </div>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel className="border-[#E2E8DF] text-[#5C6B64]" data-testid="report-cancel">Cancel</AlertDialogCancel>
          <Button onClick={handleExport} disabled={exporting || selectedOutlets.length === 0}
            className="bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white"
            data-testid="report-download">
            {exporting ? (
              <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Generating...</>
            ) : (
              <><Download className="w-4 h-4 mr-1.5" /> Download {exportFormat.toUpperCase()}</>
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
